<template>
  <v-dialog v-model="showDialog" persistent max-width="500px">
    <v-card data-testid="edit-payment-dialog">
      <v-card-title>
        <div class="d-flex align-center">
          <h1
            v-if="
              updateSucceeded &&
              subscription &&
              subscription['status'] != 'active'
            "
          >
            Subscription Renewed!
          </h1>
          <h1 v-else-if="updateSucceeded">Payment Info Updated</h1>
          <h1 v-else>Edit Payment Info</h1>
          <v-spacer /><v-progress-circular
            v-show="loading"
            :active="true"
            :indeterminate="true"
            size="25"
            width="2"
            class="mr-2"
          />
          <v-btn variant="plain" icon @click="handleCancel" :disabled="loading"
            ><v-icon icon="close" /></v-btn
          ></div></v-card-title
      >
      <v-divider />
      <v-card-text
        v-if="
          updateSucceeded && subscription && subscription['status'] != 'active'
        "
      />
      <v-card-text v-else-if="updateSucceeded" />
      <v-card-text v-else class="mt-4">
        <i
          v-show="cardError"
          class="text-primary mb-1"
          data-testid="edit-payment-error"
        >
          {{ cardError }}
        </i>
        <!--
          Stripe Elements mounts an iframe into each of these divs. Raw PAN
          never enters the DOM of this page. See #733.
        -->
        <div class="stripe-field-label">Card Number</div>
        <div
          id="stripe-form-card-number"
          class="stripe-element"
          data-testid="edit-payment-card-number"
        ></div>
        <div class="d-flex mt-3">
          <div class="flex-1 mr-2">
            <div class="stripe-field-label">Expiration</div>
            <div id="stripe-form-card-expiry" class="stripe-element"></div>
          </div>
          <div class="flex-1">
            <div class="stripe-field-label">CVC</div>
            <div id="stripe-form-card-cvc" class="stripe-element"></div>
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="py-3 px-3" v-if="updateSucceeded">
        <v-spacer />
        <v-btn
          variant="flat"
          color="primary"
          class="text-white"
          @click="handleCancel"
        >
          Okay
        </v-btn>
      </v-card-actions>
      <v-card-actions class="py-3 px-3" v-else>
        <v-btn variant="flat" @click="handleCancel" :disabled="loading">
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          variant="flat"
          color="primary"
          class="text-white"
          data-testid="edit-payment-submit"
          @click="updatePaymentMethod"
          :disabled="!cardComplete || !stripe || loading"
        >
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { loadStripe } from "@stripe/stripe-js";
import { resolvePublishableKey } from "@/services/stripePublishableKey";
import { updateBillingCard } from "@/services/billingService";

// Inline message for the billing endpoint's one refusal (HTTP 409). It means
// "nothing was changed"; it is not an error to raise globally. There is no
// "set up a bank account first" refusal: the server provisions the billing
// Customer when a card is the first billing method saved (reverses #3866).
const REFUSAL_MESSAGES = {
  ach_active:
    "Your ThreadFlow billing is paid by bank account (ACH), so a card " +
    "can't be added while bank billing is active.",
};

export default {
  name: "EditPaymentInfoDialog",

  props: {
    subscription: {},
  },

  data() {
    return {
      showDialog: false,
      // Stripe SDK instance (kept warm across open/close to avoid re-loading
      // the script on reopen).
      stripe: null,
      // Stripe Element instances. Null when the dialog is closed.
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      // Per-field validation flags driven by `.on('change', ...)` handlers.
      cardNumberComplete: false,
      cardExpiryComplete: false,
      cardCvcComplete: false,
      cardError: null,
      loading: false,
      updateSucceeded: false,
      // The key `this.stripe` was loaded with. This dialog updates the card
      // a distributor pays THREADFLOW with, on the billing Customer, which
      // lives on the distributor's `stripe_platform_mode` platform (the
      // backend calls through `client_for(mode)`). A PaymentMethod exists
      // only on the platform whose key tokenized it, so the key comes from
      // the plan payload's `stripe_publishable_key`; the build-time key is
      // only the fallback when the server has none configured.
      stripePublishableKey: null,
    };
  },

  computed: {
    cardComplete() {
      return (
        this.cardNumberComplete &&
        this.cardExpiryComplete &&
        this.cardCvcComplete
      );
    },
  },

  watch: {
    async showDialog(newVal) {
      // Only init on open, and only when we don't already have the elements
      // mounted. Guards against double-init if the dialog is toggled quickly.
      if (newVal && !this.cardNumber) {
        await this.$nextTick();
        await this.initStripeElements();
      }
    },
  },

  methods: {
    show() {
      this.showDialog = true;
    },

    async initStripeElements() {
      const key = resolvePublishableKey(this.subscription);
      if (!this.stripe || this.stripePublishableKey !== key) {
        // (Re)load when the key changed -- e.g. a different distributor is
        // now impersonated. A Stripe instance is bound to one platform.
        this.stripe = await loadStripe(key);
        this.stripePublishableKey = key;
      }
      if (!this.stripe) {
        // loadStripe returns null if the script fails to load (e.g. blocked
        // by a content filter). Surface an inline error rather than silently
        // leaving the dialog in a broken state.
        this.cardError =
          "Unable to load our payment processor. Please refresh and try again.";
        return;
      }

      const elements = this.stripe.elements();
      const style = {
        base: {
          fontSize: "16px",
          color: "#32325d",
          fontFamily: '"Roboto", sans-serif',
          "::placeholder": { color: "#aab7c4" },
        },
        invalid: { color: "#fa755a", iconColor: "#fa755a" },
      };

      this.cardNumber = elements.create("cardNumber", { style });
      this.cardNumber.mount("#stripe-form-card-number");
      this.cardNumber.on("change", (e) => {
        this.cardNumberComplete = e.complete;
        this.cardError = e.error ? e.error.message : null;
      });

      this.cardExpiry = elements.create("cardExpiry", { style });
      this.cardExpiry.mount("#stripe-form-card-expiry");
      this.cardExpiry.on("change", (e) => {
        this.cardExpiryComplete = e.complete;
        this.cardError = e.error ? e.error.message : null;
      });

      this.cardCvc = elements.create("cardCvc", { style });
      this.cardCvc.mount("#stripe-form-card-cvc");
      this.cardCvc.on("change", (e) => {
        this.cardCvcComplete = e.complete;
        this.cardError = e.error ? e.error.message : null;
      });
    },

    teardownElements() {
      // Destroy each element. Leave `this.stripe` in place — the SDK
      // instance is reusable and skipping the reload makes reopen ~instant.
      if (this.cardNumber) this.cardNumber.destroy();
      if (this.cardExpiry) this.cardExpiry.destroy();
      if (this.cardCvc) this.cardCvc.destroy();
      this.cardNumber = null;
      this.cardExpiry = null;
      this.cardCvc = null;
      this.cardNumberComplete = false;
      this.cardExpiryComplete = false;
      this.cardCvcComplete = false;
      this.cardError = null;
    },

    handleCancel() {
      this.teardownElements();
      this.showDialog = false;
      this.updateSucceeded = false;
    },

    handleDelete() {
      this.teardownElements();
      this.$emit("cancel-subscription");
      this.showDialog = false;
    },

    async updatePaymentMethod() {
      if (!this.cardComplete || !this.stripe) return;
      this.loading = true;
      this.cardError = null;
      try {
        const { paymentMethod, error } = await this.stripe.createPaymentMethod({
          type: "card",
          card: this.cardNumber,
          // TODO(#733-followup): wire billing_details from the account when
          // the distributor-profile refactor exposes a canonical source.
          billing_details: {},
        });
        if (error) {
          this.cardError = error.message;
          return;
        }
        // ONLY the new card. The server resolves the billing Customer (and
        // the old card to retire) from the caller's own subscription.
        await updateBillingCard(paymentMethod.id);
        this.updateSucceeded = true;
        // Emit using Stripe-sourced card metadata so the parent can update
        // its locally-cached display (last-4, expiry) without a reload.
        // Field names match what `paymentMethodUpdated` callers already read.
        const card = paymentMethod && paymentMethod.card ? paymentMethod.card : {};
        this.$emit("update-payment-method", {
          last4: card.last4,
          cardExpiryMonth: card.exp_month,
          cardExpiryYear: card.exp_year,
        });
      } catch (err) {
        const status = err && err.response && err.response.status;
        const data = (err && err.response && err.response.data) || {};
        if (status === 409 && REFUSAL_MESSAGES[data.status]) {
          // A deliberate refusal (bank billing active). Nothing changed; say why inline.
          this.cardError = REFUSAL_MESSAGES[data.status];
        } else if (status === 404) {
          // No billing tenant: a Super Admin who is not impersonating, or a
          // distributor with no subscription. The card belongs to a
          // distributor's subscription, so there is nothing to update.
          this.cardError =
            "No ThreadFlow subscription was found. (Super Admins: impersonate " +
            "the distributor to update its billing card.)";
        } else if (status === 402) {
          // CardError from Stripe — show the sanitized message inline.
          this.cardError =
            data.message ||
            "Your card was declined. Please try a different card.";
        } else if (status === 502) {
          // Stripe API failure (timeout, outage, etc.).
          this.cardError =
            "Unable to reach our payment processor. Please try again in a moment.";
        } else {
          // Unexpected. Log + raise a global toast as a backstop.
          this.cardError =
            "Something went wrong updating your payment method. Please try again.";
          console.error(describeError(err));
          this.$store.dispatch("raiseError", err);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.stripe-element {
  min-height: 40px;
  padding: 12px;
  border: 1px solid rgba(0, 0, 0, 0.42);
  border-radius: 4px;
  background: white;
  transition: border-color 0.2s;
}
.stripe-element:hover {
  border-color: rgba(0, 0, 0, 0.87);
}
.stripe-element.StripeElement--focus {
  border-color: #1976d2;
}
.stripe-field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}
.flex-1 {
  flex: 1;
}
</style>
